import {ITransRow,ETransactionType,TransactionTypes,TransactionTypeNames,ValidTransactionTypes,TransFields} from "./ITransaction"
import {IPrice, TFrequency, Currency } from "./IPrice";
import {IAccount, EAccountTypes } from "./IAccount";
import {IPortUser} from "./IPortUser"
import Holding from "../models/Holding";

export { type ITransRow }
export { ETransactionType as TransactionType, TransactionTypes, TransactionTypeNames, ValidTransactionTypes, TransFields }

export { type IPrice, type TFrequency, Currency }
export { type IAccount, EAccountTypes }
export { type IPortUser }

export enum ESectors {
    Mixed           = -1,
    Cash            = 0,
    FixedIncome     = 1,
    Technology      = 2,
    Financial       = 3,
    Discretionary   = 4,
    Industrial      = 5,
    Healthcare      = 6,
    Communications  = 7,
    Utilities       = 8,
    Energy          = 9,
    Materials       = 10,
    Defensive       = 11,
    RealEstate      = 12,
}

export const Sectors = [
    {id:ESectors.Materials, text:"Basic Materials"},
    {id:ESectors.Communications, text:"Communication Services"},
    {id:ESectors.Cash, text:"Cash"},
    {id:ESectors.Defensive, text:"Consumer Defensive"},
    {id:ESectors.Discretionary, text:"Cyclical"},
    {id:ESectors.Financial, text:"Financial Services"},
    {id:ESectors.FixedIncome, text:"Fixed Income"},
    {id:ESectors.Energy, text:"Energy"},
    {id:ESectors.Healthcare, text:"Heathcare"},
    {id:ESectors.Industrial, text:"Industrials"},
    {id:ESectors.Mixed, text:"Mixed"},
    {id:ESectors.RealEstate, text:"Real Esates"},
    {id:ESectors.Technology, text:"Technology"},
    {id:ESectors.Utilities, text:"Utilities"},
]

// sector,portion
export type TSectorPair = [ESectors,number]

export const Cols = {
    id:"id",
    holdingId:"holdingId",
    name:"name",
    sector:"sector",
    quantity:"quantity",
    averagePrice:"averagePrice",
    dividend:"dividend",
    gain:"gain",
    adjustments:"adjustments",
    currency:"currency",
    currentPrice:"currentPrice",
    mwPercent:"mwPercent",
    holdingValue:"holdingValue",
    holdingCost:"holdingCost",
    price:"price",
    date:"date",
    accountId:"accountId",
    maturity:"maturity",
    maturityMonth:"mm",
    payout:"payout",
    total:"total",               // qty*price
    type:"type",
    originalCost:"originalCost",
    holdingPercent:"holdingPercent",
    holdingPortion:"holdingPortion",
    note:"note",
    gicId:"gicId",
    yield:"yield",
    a_payout:"a_payout",
    n_payout:"n_payout",
    ytd_payout:"ytd_payout",
    excessDiv:"excessDiv",
    profit:"profit",
    ugain:"ugain",
    balance:"balance",
}

export enum PriceCols {
    ticker="_id",
    currency="currency",
    price="price",
    name="name",
    payout="payout",
    yield="yield",
    frequency="frequency",
    sector="sector",
    date="date",
    variableDividend="vd",
    exDividend="ex",
    sectors="sectors"
}

export const CashEqRegex = /^(GIC|SAVE|BOND)\-(US|CA)\-/
export const FixedIncomePrices : Record<string,IPrice> = {
    "GIC":{_id:"GIC",price:1,currency:0,sector:ESectors.FixedIncome,frequency:"a"},
    "GIC.CA":{_id:"GIC",price:1,currency:0,sector:ESectors.FixedIncome,frequency:"a"},
    "GIC.US":{_id:"GIC.US",price:1,currency:1,sector:ESectors.FixedIncome,frequency:"a"},

    "CASH":{_id:"CASH",price:1,currency:0,sector:ESectors.Cash,frequency:"m"},
    "CASH.CA":{_id:"CASH",price:1,currency:0,sector:ESectors.Cash,frequency:"m"},
    "CASH.US":{_id:"CASH.US",price:1,currency:1,sector:ESectors.Cash,frequency:"m"},

    "BOND":{_id:"BOND",price:1,currency:0,sector:ESectors.FixedIncome,frequency:"s"},
    "BOND.CA":{_id:"BOND",price:1,currency:0,sector:ESectors.FixedIncome,frequency:"s"},
    "BOND.US":{_id:"BOND.US",price:1,currency:1,sector:ESectors.FixedIncome,frequency:"s"},

    "SAVE":{_id:"SAVE",price:1,currency:0,sector:ESectors.Cash,frequency:"m"},
    "SAVE.CA":{_id:"SAVE",price:1,currency:0,sector:ESectors.Cash,frequency:"m"},
    "SAVE.US":{_id:"SAVE.US",price:1,currency:1,sector:ESectors.Cash,frequency:"m"}
}

export interface ICompValue {
    compValue(col:string) : string|number|Date
}

// if comp value is the same, compare date/quantity
export interface ICompValue2 extends ICompValue {
    date:Date
    quantity:number
}

export type TFilter = { account:number, currency:number, filter:string, showDisposed:boolean, regex:RegExp }
export interface IValue { value(col:string) : string|number }

export interface IHolding { 
    id:string
    holdingCost:number
    holdingValue:number
    dividend:number
    a_payout:number
    yield:number
    profit:number
    gain:number
    mwPercent:number
    holdingPercent:number
    currency:number
    ugain:number
}

export interface ITransaction {
    _id:string
    type:ETransactionType
    date:Date
    quantity:number
    accountId:number
    price:number
    note:string
    yield?:number
    maturity?:Date
    holding?:Holding
    gain:number
}

export interface IPriceHistory {
    _id:string
    history:Record<string,number>
}

export function isCash(ticker:string) {
    return /^CASH/.test(ticker)
}
export function isSave(ticker:string|null|undefined) {
    return !!ticker && /^SAVE\-/.test(ticker)
}
export function isGIC(ticker:string|null|undefined){
    return !!ticker && /^GIC\-/.test(ticker)
}
export function isBond(ticker:string|null|undefined){
    return !!ticker && /^BOND\-/.test(ticker)
}
export function isCashEq(ticker:string){
    return isCash(ticker) || isGIC(ticker) || isBond(ticker) || isSave(ticker)
}
export type THoldingTypes = "cash"|"equity"|"fixed"|"save"
export function getHoldingType(ticker:string) : THoldingTypes {
    if (isCash(ticker)) return "cash"
    else if (isSave(ticker)) return "save"
    else if (isGIC(ticker) || isBond(ticker)) return "fixed"
    else return "equity"
}